import { NavLink, useNavigate } from "react-router-dom";
import { BotStatusButton } from "@/components/BotStatus";
import {
  LayoutDashboard, Hash, Shield, Users, MessageSquareText, BarChart3, DatabaseBackup,
  Settings, LogOut, Bot, History, UserCheck, Wrench, ShieldAlert, Zap, SquareSlash, MessageCircleReply, ListChecks, Sparkles, Rss, Trophy, Coins, Gift, Lightbulb, Cake, Ticket, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";

const GROUPS = [
  { title: null, items: [{ to: "/", label: "Panoramica", icon: LayoutDashboard, id: "panoramica", end: true, perm: "overview" }] },
  { title: "Struttura", items: [
    { to: "/canali", label: "Canali", icon: Hash, id: "canali", perm: "channels" },
    { to: "/ruoli", label: "Ruoli", icon: Shield, id: "ruoli", perm: "roles" },
    { to: "/manutenzione", label: "Ripristino ruoli", icon: Wrench, id: "manutenzione", perm: "roles" },
  ]},
  { title: "Gestione", items: [
    { to: "/membri", label: "Membri", icon: Users, id: "membri", perm: "members" },
    { to: "/moderazione", label: "Moderazione", icon: ShieldAlert, id: "moderazione", perm: "members" },
    { to: "/ticket", label: "Ticket", icon: Ticket, id: "ticket", perm: "settings" },
    { to: "/verifica", label: "Verifica ingresso", icon: ShieldCheck, id: "verifica", perm: "settings" },
    { to: "/embed", label: "Embed Builder", icon: MessageSquareText, id: "embed-builder", perm: "embed" },
  ]},
  { title: "Automazioni", items: [
    { to: "/automazioni", label: "Funzioni automatiche", icon: Zap, id: "automazioni", perm: "settings" },
    { to: "/ruoli-verifica", label: "Menu ruoli", icon: ListChecks, id: "ruoli-verifica", perm: "settings" },
    { to: "/risposte", label: "Risposte e comandi", icon: MessageCircleReply, id: "risposte", perm: "settings" },
    { to: "/comandi", label: "Comandi slash", icon: SquareSlash, id: "comandi", perm: "settings" },
    { to: "/canali-auto", label: "Canali intelligenti", icon: Sparkles, id: "canali-auto", perm: "settings" },
    { to: "/notifiche", label: "Notifiche social", icon: Rss, id: "notifiche", perm: "settings" },
  ]},
  { title: "Community", items: [
    { to: "/livelli", label: "Livelli", icon: Trophy, id: "livelli", perm: "settings" },
    { to: "/economia", label: "Economia", icon: Coins, id: "economia", perm: "settings" },
    { to: "/giveaway", label: "Giveaway e sondaggi", icon: Gift, id: "giveaway", perm: "settings" },
    { to: "/suggerimenti", label: "Suggerimenti", icon: Lightbulb, id: "suggerimenti", perm: "settings" },
    { to: "/extra", label: "Compleanni e altro", icon: Cake, id: "extra", perm: "settings" },
  ]},
  { title: "Analisi", items: [{ to: "/statistiche", label: "Statistiche", icon: BarChart3, id: "statistiche", perm: "statistics" }] },
  { title: "Sicurezza", items: [
    { to: "/backup", label: "Backup", icon: DatabaseBackup, id: "backup", perm: "backups" },
    { to: "/registro", label: "Registro attività", icon: History, id: "registro", perm: "overview" },
  ]},
  { title: "Sistema", items: [
    { to: "/ammissioni", label: "Ammissioni", icon: UserCheck, id: "ammissioni", ownerOnly: true },
    { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni", perm: "settings" },
  ]},
];

export function Sidebar({ onNavigate }) {
  const { logout, demoMode, can, isOwner, user } = useAuth();
  const { guild } = useData();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };

  const linkClass = ({ isActive }) =>
    `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200 ${
      isActive ? "bg-gradient-to-r from-[#5865F2]/25 to-[#8B5CF6]/10 font-semibold text-white shadow-[0_0_18px_rgba(88,101,242,0.18)]"
               : "font-medium text-slate-400 hover:bg-white/5 hover:text-white"}`;

  return (
    <aside className="glass flex h-full w-64 flex-col border-r border-white/5" data-testid="app-sidebar">
      <div className="flex items-center gap-3 px-4 py-5">
        <div className="relative">
          {guild?.icon ? <img src={guild.icon} alt="" className="h-11 w-11 rounded-2xl object-cover ring-2 ring-white/10" />
            : <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#5865F2] to-[#8B5CF6]"><Bot className="h-5 w-5 text-white" /></div>}
          <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-[#12141D] bg-[#23A55A] shadow-[0_0_8px_#23A55A]" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-extrabold tracking-tight text-white" data-testid="sidebar-guild-name">{guild?.name || "Server"}</p>
          <p className="truncate text-[11px] text-slate-500">ID {guild?.id || "—"}</p>
        </div>
      </div>
      <div className="mx-4 mb-1 h-px bg-white/5" />

      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-3">
        {GROUPS.map((g, gi) => {
          const items = g.items.filter((it) => (it.ownerOnly ? isOwner : (it.perm ? can(it.perm) : true)));
          if (items.length === 0) return null;
          return (
            <div key={gi}>
              {g.title && <p className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-600">{g.title}</p>}
              <div className="space-y-1">
                {items.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`sidebar-nav-${n.id}`} className={linkClass}>
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-[#5865F2] shadow-[0_0_10px_#5865F2]" />}
                        <n.icon className={`h-[18px] w-[18px] ${isActive ? "text-[#8ea0ff]" : "text-slate-500 group-hover:text-white"}`} />
                        <span className="truncate">{n.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="mx-3 mb-2 space-y-1.5">
        {demoMode && <div className="rounded-xl border border-[#FEE75C]/20 bg-[#FEE75C]/10 px-3 py-1.5 text-xs font-semibold text-[#FEE75C]">● Modalità demo</div>}
        <BotStatusButton className="w-full justify-start" />
      </div>
      <div className="mx-4 h-px bg-white/5" />
      <div className="flex items-center gap-3 p-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#5865F2] to-[#8B5CF6] text-sm font-bold text-white">
          {(user?.displayName || "A").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">{user?.displayName || "Account"}</p>
          <p className="truncate text-[11px] text-slate-500">{isOwner ? "Amministratore" : "Collaboratore"}</p>
        </div>
        <button onClick={doLogout} data-testid="logout-button" title="Esci" className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-[#F23F43]/10 hover:text-[#F23F43]">
          <LogOut className="h-[18px] w-[18px]" />
        </button>
      </div>
    </aside>
  );
}
